/* eslint-disable max-len */
import React from 'react';
import classNames from 'classnames';
import PropTypes from 'prop-types';
import { Link } from '@one-thd/sui-atomic-components';
import KeyProductFeatureName from './KeyProductFeatureName';
import { KeyProductFeatureValue } from './KeyProductFeatureValue';

const specBlock = (feature, simple, inverseText, responsiveTileGrid) => (
  <div
    className={classNames(
      'sui-flex',
      {
        'sui-flex-col sui-gap-0 sui-border-1 sui-border-solid sui-border-primary sui-rounded-lg sui-p-2 sui-bg-primary': responsiveTileGrid,
        'sui-items-center sui-gap-1': simple && !responsiveTileGrid,
        'sui-flex-col sui-gap-3': !simple && !responsiveTileGrid,
      }

    )}
  >
    <KeyProductFeatureName name={feature.name} simple={simple} inverseText={inverseText} responsiveTileGrid={responsiveTileGrid} />
    <KeyProductFeatureValue value={feature.value} simple={simple} inverseText={inverseText} responsiveTileGrid={responsiveTileGrid} />
  </div>
);

const similarItemBlock = (feature, simple, inverseText, responsiveTileGrid) => (
  <div className="sui-flex sui-flex-col sui-gap-3">
    {specBlock(feature, simple, inverseText, responsiveTileGrid)}
    <Link
      href={feature.refinementUrl}
      data-testid="similar-items-link"
      variant={simple ? 'body-sm' : 'body-base'}
    >
      See Similar Items
    </Link>
  </div>
);

const keyProductFeatureSpecs = (featuresAttr, maxfeatures, displaySimilarLinks, simple, inverseText, responsiveTileGrid) => (
  featuresAttr.slice(0, maxfeatures).map((feature, index) => (
    <div
      key={feature.refinementId}
      className={classNames(
        simple || responsiveTileGrid ? '' : 'sui-py-6',
        responsiveTileGrid && (featuresAttr.slice(0, maxfeatures).length < 4 || index === 0) ? 'sui-col-span-2' : ''
      )}
    >
      {feature.refinementUrl && feature.value.length === 1 && displaySimilarLinks ? (
        <a
          className="hover:sui-no-underline"
          href={feature.refinementUrl}
          rel="noopener noreferrer"
          target="_blank"
        >
          {similarItemBlock(feature, simple, inverseText, responsiveTileGrid)}
        </a>
      ) : (
        <div className="sui-w-full">
          {specBlock(feature, simple, inverseText, responsiveTileGrid)}
        </div>
      )}
    </div>
  ))
);

const KeyProductFeatureContents = ({
  url = '',
  featuresAttr = [],
  oneColumnGrid = false,
  twoColumnGrid = false,
  simple = false,
  forceMobile = false,
  maxfeatures = 8,
  displaySimilarLinks = false,
  inverseText = false,
  responsiveTileGrid = false
}) => {
  const specClasses = classNames(
    'sui-grid',
    {
      'sui-bg-strong sui-p-3': responsiveTileGrid,
      'sui-gap-2': simple || responsiveTileGrid,
      'sui-gap-6': !simple && !responsiveTileGrid,
      'sui-grid-cols-1 md:sui-grid-cols-2 lg:sui-grid-cols-3': !oneColumnGrid && !twoColumnGrid && !responsiveTileGrid,
      'sui-grid-cols-1': oneColumnGrid || (responsiveTileGrid && featuresAttr.slice(0, maxfeatures).length < 4),
      'sui-grid-cols-2': twoColumnGrid || (responsiveTileGrid && featuresAttr.slice(0, maxfeatures).length >= 4)
    }
  );

  const defaultClasses = classNames('sui-flex sui-flex-col lg:sui-flex-row sui-w-full sui-gap-6 sui-overflow-hidden');
  const mobileClasses = classNames('sui-flex sui-flex-col sui-w-full sui-gap-6 sui-overflow-hidden');

  return (
    <div
      data-component="KeyProductFeatures"
      className={forceMobile ? mobileClasses : defaultClasses}
      data-testid={forceMobile ? 'kpf-mobile' : 'kpf-desktop'}
    >
      {url && (
        <div className="sui-flex sui-justify-center sui-items-center sui-min-w-fit">
          <img
            className="sui-object-contain sui-w-96 lg:sui-w-80"
            src={url || ''}
            alt="product"
            height="1"
            width="1"
          />
        </div>
      )}
      <div className="sui-grow">
        <div className={specClasses} data-testid="key-product-feature-list">
          {keyProductFeatureSpecs(featuresAttr, maxfeatures, displaySimilarLinks, simple, inverseText, responsiveTileGrid)}
        </div>
      </div>
    </div>
  );
};

KeyProductFeatureContents.propTypes = {
  url: PropTypes.string,
  featuresAttr: PropTypes.arrayOf(PropTypes.shape({
    refinementId: PropTypes.string.isRequired,
    name: PropTypes.string.isRequired,
    value: PropTypes.oneOfType([
      PropTypes.string,
      PropTypes.arrayOf(PropTypes.string)
    ]).isRequired,
    refinementUrl: PropTypes.string
  })),
  oneColumnGrid: PropTypes.bool,
  twoColumnGrid: PropTypes.bool,
  simple: PropTypes.bool,
  forceMobile: PropTypes.bool,
  maxfeatures: PropTypes.number,
  displaySimilarLinks: PropTypes.bool,
  inverseText: PropTypes.bool,
  responsiveTileGrid: PropTypes.bool
};

export default KeyProductFeatureContents;
